import { checkForSelectValue } from '../form-helpers';
import useAuth from '../../../hooks/auth-hooks';
import { ITerminalInsertUpdate } from '../../../models/terminal.interface';

/**
 * Function to validate the form and check for select values
 * 
 * @param form HTMLFormElement
 * @param selectElements HTMLElement[]
 * @returns boolean
 */
export const validateForm = (form: HTMLFormElement, selectElements: HTMLElement[]) => {
  form.classList.add('was-validated');

  selectElements.forEach((element, index) => {
    const label = element?.getAttribute('data-label');
    const ele = element?.getAttribute('data-ele');
    //@ts-ignore
    checkForSelectValue(element, label, ele);
  });

  const allValid = selectElements.every(element => element.getAttribute('data-value') != null);

  return form.checkValidity() && allValid;
};

/**
 * Gets the formatted notes from the notes textarea
 * 
 * @param notes HTMLTextAreaElement | null
 * @param defaultNotes string
 * @returns string
 */
const getFormattedNotes = (notes: HTMLInputElement | null, defaultNotes: string) => {
  return notes ? escapeSpecialCharacters(notes.value) : defaultNotes;
};

const escapeSpecialCharacters = (str: string) => {
  return str.replace(/"/g, '\\"').replace(/\n/g, '\\n');
}

/**
 * Get the value of an attribute from an HTML element
 * 
 * @param element HTMLElement
 * @param attribute string
 * @returns string | null
 */
const getAttributeValue = (element: HTMLElement, attribute: string) => {
  return element ? element.getAttribute(attribute) : null;
};

/**
 * Get the terminal data from the form elements 
 * 
 * @param terminalID string
 * @param terminalData any
 * @param formElements any
 * @returns ITerminalInsertUpdate
 */
export const getTerminalData = (terminalID: string, terminalData: any, formElements: any): ITerminalInsertUpdate => {
  const { terminalPartner, terminalPublic, terminalNotes, terminalInternalNotes } = formElements;
  const partnerData = getAttributeValue(terminalPartner, 'data-value')!;
  const { userId } = useAuth()!;
  const formattedTerminalNotes = getFormattedNotes(terminalNotes, '');
  const formattedInternalNotes = getFormattedNotes(terminalInternalNotes, terminalData[0]?.internalNotes || null);
  const publicPrivate = getAttributeValue(terminalPublic, 'data-value');
  const terminalPublicValue = publicPrivate === null ? null : publicPrivate === 'Public' ? 1 : 0;
  const national = formElements.terminalNational ? formElements.terminalNational.checked : null;
  const outOfService = formElements.terminalOutOfService ? formElements.terminalOutOfService.checked : null;

  return {
    iuUserId: userId,
    iuTerminalId: terminalID,
    iuPartnerId: +partnerData,
    iuTerminalName: formElements.terminalDescription.value,
    iuCostCents: formElements.terminalCostCents.valueAsNumber,
    iuDefaultMargin: formElements.terminalMarginCents.valueAsNumber,
    iuLimitGallons: formElements.terminalGallonLimit.valueAsNumber,
    iuAddressOne: formElements.terminalAddressOne.value,
    iuAddressTwo: formElements.terminalAddressTwo.value,
    iuAddressCity: formElements.terminalAddressCity.value,
    iuAddressState: String(getAttributeValue(formElements.terminalAddressState, 'data-value')),
    iuAddressZip: formElements.terminalAddressZip.value,
    iuStatus: Number(getAttributeValue(formElements.terminalStatus, 'data-value')) || null,
    iuPublicTerminal: terminalPublicValue,
    iuNational: national === null ? null : formElements.terminalNational.checked ? '1' : '0',
    iuOutOfService: outOfService === null ? null : formElements.terminalOutOfService?.checked ? 1 : 0,
    iuBillingType: getAttributeValue(formElements.terminalBillingType, 'data-value'),
    iuTransactionFee: formElements.terminalTransactionFee?.valueAsNumber || null,
    iuTerminalFee: formElements.terminalFee?.valueAsNumber || null,
    iuRegion: formElements.terminalRegion.value,
    iuServicingBranch: formElements.terminalSvcBranch.value,
    iuCustomerList: formElements.terminalCustList.value.replace(/\n/g, '\\n'),
    iuContactName: formElements.terminalContactName.value,
    iuContactPhone: formElements.terminalContactPhone.value,
    iuContactEmail: formElements.terminalContactEmail.value,
    iuContactNameTwo: formElements.terminalContactNameTwo.value,
    iuContactPhoneTwo: formElements.terminalContactPhoneTwo.value,
    iuContactEmailTwo: formElements.terminalContactEmailTwo.value,
    iuContactNameThree: formElements.terminalContactNameThree.value,
    iuContactPhoneThree: formElements.terminalContactPhoneThree.value,
    iuContactEmailThree: formElements.terminalContactEmailThree.value,
    iuNotes: formattedTerminalNotes,
    iuTerminalSerialNum: formElements.terminalSerialNum?.value || terminalData[0]?.serialNumber || null,
    iuIpAddress: formElements.terminalIpAddress?.value || terminalData[0]?.ipAddress || null,
    iuOrderedDate: formElements.terminalOrderedDate?.value || null,
    iuMoniterSerialNumber: formElements.terminalMonitorSerialNumber?.value || null,
    iuGpsLatitude: formElements.terminalGPSLatitude?.value || null,
    iuGpsLongitude: formElements.terminalGPSLongitude?.value || null,
    iuInternalNotes: formattedInternalNotes,
    iuAccessDescription: formElements.terminalAccessDesc.value
  };
};